import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import SectionHeading from '@/components/base/SectionHeading';
import { getMoviesByIds } from '@/lib/catalog';
import { specials } from '@/mocks/specials';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function SpecialsPage() {
  useDocumentMeta({
    title: '午夜电影网 - 电影专题合集',
    description:
      '午夜电影网主题策展专题，把气质相近的电影放在一起：深夜独处、时间与记忆、雨夜黑色电影、华语科幻与盛夏成长。',
    keywords: '电影专题,电影合集,主题策展,片单推荐,午夜电影网',
  });

  return (
    <div>
      <section className="relative overflow-hidden border-b border-background-200 bg-accent-100 pb-10 pt-24 md:pb-14 md:pt-32">
        <div className="absolute -right-16 top-6 h-72 w-72 rounded-full bg-primary-500/10 blur-3xl" />
        <div className="shell relative">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '专题合集' }]} />
          <div className="mt-5 max-w-3xl">
            <p className="eyebrow text-[11px] text-primary-500">Curated by Midnight</p>
            <h1 className="mt-2 font-heading text-3xl font-semibold tracking-wide text-foreground-950 md:text-5xl">
              专题合集
            </h1>
            <p className="mt-3 text-sm leading-7 text-foreground-500">
              我们不做无意义的排行榜。每一个专题都有一个明确的情绪或命题，把相关的电影放进同一个语境里，让它们互相照亮。
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-layout-grid-line text-primary-500" aria-hidden="true"></i>
              {specials.length} 个主题专题
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-primary-500" aria-hidden="true"></i>
              每月持续更新
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-heart-line text-primary-500" aria-hidden="true"></i>
              编辑部人工策展
            </span>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="shell">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {specials.map((item) => {
              const movies = getMoviesByIds(item.movieIds).slice(0, 3);
              return (
                <Link
                  key={item.id}
                  to={`/specials/${item.id}`}
                  className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-500/50"
                >
                  <div className="relative h-48 w-full overflow-hidden md:h-52">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} - 主题电影专题`}
                      className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-background-100/20 to-transparent" />
                    <span className="absolute left-3 top-3 rounded bg-primary-500 px-2 py-0.5 text-[10px] font-medium text-background-50">
                      {item.tag}
                    </span>
                  </div>

                  <div className="flex flex-1 flex-col p-4 md:p-5">
                    <p className="text-[10px] tracking-[0.24em] text-primary-500">{item.englishTitle}</p>
                    <h2 className="mt-1.5 font-heading text-xl tracking-wide text-foreground-950 transition-colors group-hover:text-primary-500">
                      {item.title}
                    </h2>
                    <p className="mt-2 line-clamp-2 flex-1 text-xs leading-6 text-foreground-500">{item.description}</p>

                    <div className="mt-4 flex items-center justify-between border-t border-background-200/80 pt-3">
                      <div className="flex -space-x-2">
                        {movies.map((movie) => (
                          <span
                            key={movie.id}
                            className="h-10 w-7 overflow-hidden rounded border border-background-100 bg-background-200"
                          >
                            <img src={movie.poster} alt={movie.title} className="h-full w-full object-cover object-top" />
                          </span>
                        ))}
                      </div>
                      <span className="flex items-center gap-1 whitespace-nowrap text-[11px] text-foreground-500">
                        {item.movieIds.length} 部影片
                        <i className="ri-arrow-right-line text-primary-500 transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="mt-14 rounded-lg border border-background-200 bg-background-100 px-6 py-8 md:px-10">
            <SectionHeading
              eyebrow="How we curate"
              title="专题是怎么做出来的"
              description="先从一种情绪开始，再去找能承载它的电影。我们尽量避开重复的片单，也尽量不让同一个专题里出现两部气质相撞的作品。所有专题封面与文字均为演示内容。"
              actionLabel="浏览电影片库"
              actionTo="/movies"
            />
          </div>
        </div>
      </section>
    </div>
  );
}